import React, { useState, useEffect } from 'react';
import { Table, Button, Drawer, Form, Input, Select, Space, Popconfirm, message, Tag } from 'antd';
import { EditOutlined, DeleteOutlined, KeyOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import rbacService from '../../services/rbacService';
import AssignPermissionsDrawer from './AssignPermissionsDrawer';
import useBreakpoint from '../../hooks/useBreakpoint';

const { TextArea } = Input;
const { Option } = Select;

export default function RolesManagement() {
  const [roles, setRoles] = useState([]);
  const [hierarchyLevels, setHierarchyLevels] = useState([]);
  const [entityTypes, setEntityTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [assignPermissionsVisible, setAssignPermissionsVisible] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [tableSearchText, setTableSearchText] = useState('');
  const [form] = Form.useForm();
  const isAboveSm = useBreakpoint('sm');

  const filteredRoles = tableSearchText.trim()
    ? roles.filter(
      (r) =>
        (r.name || '').toLowerCase().includes(tableSearchText.toLowerCase()) ||
        (r.description || '').toLowerCase().includes(tableSearchText.toLowerCase())
    )
    : roles;

  useEffect(() => {
    fetchRoles();
    fetchHierarchyLevels();
    fetchEntityTypes();
  }, []);

  const fetchHierarchyLevels = async () => {
    try {
      const result = await rbacService.getHierarchyLevels();
      if (result.success && Array.isArray(result.data)) {
        setHierarchyLevels(result.data);
      } else {
        setHierarchyLevels([]);
      }
    } catch (error) {
      console.error('Error fetching hierarchy levels:', error);
      setHierarchyLevels([]);
    }
  };

  const fetchEntityTypes = async () => {
    try {
      const result = await rbacService.getEntityTypes();
      if (result.success && Array.isArray(result.data)) {
        setEntityTypes(result.data);
      } else {
        setEntityTypes([]);
      }
    } catch (error) {
      console.error('Error fetching entity types:', error);
      setEntityTypes([]);
    }
  };

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const result = await rbacService.getRoles();
      if (result.success) {
        const data = result.data;
        const rolesArray = data?.roles ?? (Array.isArray(data) ? data : []);
        setRoles(Array.isArray(rolesArray) ? rolesArray : []);
      } else {
        message.error(result.message || 'Failed to fetch roles');
        setRoles([]);
      }
    } catch (error) {
      message.error('Error loading roles');
      console.error('Error fetching roles:', error);
      setRoles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedRole(null);
    form.resetFields();
    setDrawerVisible(true);
  };

  const handleEdit = (record) => {
    setSelectedRole(record);
    form.setFieldsValue({
      name: record.name,
      description: record.description || '',
      entity_type_ids: (record.entity_types || []).map((et) => et.id),
    });
    setDrawerVisible(true);
  };

  const handleDelete = async (roleId) => {
    try {
      const result = await rbacService.deleteRole(roleId);
      if (result.success) {
        message.success('Role deleted successfully');
        fetchRoles();
      } else {
        message.error(result.message || 'Failed to delete role');
      }
    } catch (error) {
      message.error('Error deleting role');
      console.error('Error deleting role:', error);
    }
  };

  const handleAssignPermissions = (record) => {
    setSelectedRole(record);
    setAssignPermissionsVisible(true);
  };

  const handleSubmit = async (values) => {
    try {
      if (selectedRole) {
        const result = await rbacService.updateRole(selectedRole.id, {
          description: values.description,
          entity_type_ids: values.entity_type_ids || [],
        });
        if (result.success) {
          message.success('Role updated successfully');
          setDrawerVisible(false);
          fetchRoles();
        } else {
          message.error(result.message || 'Failed to update role');
        }
      } else {
        const result = await rbacService.createRole({
          name: values.name,
          description: values.description,
          level: values.level,
          entity_type_ids: values.entity_type_ids || [],
        });
        if (result.success) {
          message.success('Role created successfully');
          setDrawerVisible(false);
          fetchRoles();
        } else {
          message.error(result.message || 'Failed to create role');
        }
      }
    } catch (error) {
      message.error(selectedRole ? 'Error updating role' : 'Error creating role');
      console.error('Error saving role:', error);
    }
  };

  const columns = [
    {
      title: 'Role Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text) => <span className="font-medium">{text}</span>,
    },
    {
      title: 'Entity Types',
      key: 'entity_types',
      render: (_, record) => {
        const types = record.entity_types || [];
        if (!types.length) {
          return <Tag className="text-xs">All internal (unmapped)</Tag>;
        }
        return (
          <Space size={[4, 4]} wrap>
            {types.map((et) => (
              <Tag key={et.id || et.code} className="text-xs m-0!">{et.name || et.code}</Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text) => text || <span className="text-gray-400">No description</span>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: isAboveSm ? 260 : 200,
      fixed: isAboveSm ? 'right' : undefined,
      render: (_, record) => (
        <Space size={isAboveSm ? 'small' : 'middle'} wrap>
          {isAboveSm ? (
            <>
              <Button
                type="primary"
                icon={<KeyOutlined />}
                size="small"
                onClick={() => handleAssignPermissions(record)}
              >
                Permissions
              </Button>
              <Button
                icon={<EditOutlined />}
                size="small"
                onClick={() => handleEdit(record)}
              />
              <Popconfirm
                title="Delete Role"
                description="Are you sure you want to delete this role? This action cannot be undone."
                onConfirm={() => handleDelete(record.id)}
                okText="Yes, Delete"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
              >
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  size="small"
                />
              </Popconfirm>
            </>
          ) : (
            <>
              <Button
                type="primary"
                icon={<KeyOutlined />}
                size="middle"
                onClick={() => handleAssignPermissions(record)}
                title="Assign Permissions"
                className="!px-2"
              />
              <Button
                icon={<EditOutlined />}
                size="middle"
                onClick={() => handleEdit(record)}
                title="Edit Role"
                className="!px-2"
              />
              <Popconfirm
                title="Delete Role"
                description="Are you sure you want to delete this role? This action cannot be undone."
                onConfirm={() => handleDelete(record.id)}
                okText="Yes, Delete"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
              >
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  size="middle"
                  title="Delete Role"
                  className="!px-2"
                />
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-4 flex flex-wrap justify-between items-center gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Roles</h2>
          <p className="text-sm text-gray-600 mt-1">Create and manage user roles</p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
          size="large"
        >
          Create Role
        </Button>
      </div>

      <div className="mb-3">
        <Input.Search
          placeholder="Search by role name or description..."
          allowClear
          enterButton={
            <>
              <SearchOutlined /> Search
            </>
          }
          value={tableSearchText}
          onChange={(e) => setTableSearchText(e.target.value)}
          onSearch={setTableSearchText}
          className="max-w-md"
        />
      </div>

      <Table
        columns={columns}
        dataSource={filteredRoles}
        loading={loading}
        rowKey="id"
        scroll={{ x: 1000 }}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} roles`,
          showQuickJumper: true,
        }}
        className="rbac-table"
      />

      <style>{`
        .rbac-table .ant-table {
          font-size: 14px;
        }
        .rbac-table .ant-table-thead > tr > th {
          background: #F9FAFB;
          font-weight: 600;
          color: #374151;
          border-bottom: 2px solid #E5E7EB;
        }
        .rbac-table .ant-table-tbody > tr:hover > td {
          background: #F3F4F6;
        }
        .rbac-table .ant-pagination {
          margin-top: 24px;
        }
      `}</style>

      <Drawer
        title={selectedRole ? 'Edit Role' : 'Create New Role'}
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        width={500}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            label="Role Name"
            name="name"
            rules={[
              { required: true, message: 'Please enter role name' },
              { min: 2, message: 'Role name must be at least 2 characters' },
            ]}
          >
            <Input
              placeholder="e.g., Sales Manager"
              disabled={!!selectedRole}
            />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
            rules={[
              { max: 500, message: 'Description cannot exceed 500 characters' },
            ]}
          >
            <TextArea
              rows={4}
              placeholder="Describe the role's responsibilities..."
            />
          </Form.Item>

          <Form.Item
            label="Entity Types"
            name="entity_type_ids"
            extra="Leave empty for all internal entity types (global internal role)."
          >
            <Select
              mode="multiple"
              placeholder="Select entity types"
              allowClear
              optionFilterProp="label"
            >
              {entityTypes.map((et) => (
                <Option key={et.id || et.value} value={et.id} label={et.label || et.name}>
                  {et.label || et.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {!selectedRole && (
            <Form.Item
              label="Level"
              name="level"
              rules={[
                { required: true, message: 'Please select a hierarchy level' },
              ]}
            >
              <Select
                placeholder="Select hierarchy level"
                showSearch
                optionFilterProp="label"
                options={hierarchyLevels.map((h) => ({
                  value: h.level,
                  label: `${h.name} (Level ${h.level})`,
                }))}
              />
            </Form.Item>
          )}

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {selectedRole ? 'Update Role' : 'Create Role'}
              </Button>
              <Button onClick={() => setDrawerVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Drawer>

      {selectedRole && (
        <AssignPermissionsDrawer
          visible={assignPermissionsVisible}
          role={selectedRole}
          onClose={() => {
            setAssignPermissionsVisible(false);
            setSelectedRole(null);
          }}
          onSuccess={() => {
            fetchRoles();
          }}
        />
      )}
    </div>
  );
}
